import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    input,
    OnChanges,
    OnDestroy,
    viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import * as PlyrModule from 'plyr';

type IackathonMediaType = 'audio' | 'video';

@Component({
    selector: 'app-iackathon-media-player',
    templateUrl: './iackathon-media-player.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
    ],
})
export class IackathonMediaPlayerComponent implements AfterViewInit, OnChanges, OnDestroy {
    public src = input.required<string>();
    public mediaType = input.required<IackathonMediaType>();
    public ariaLabel = input<string>();
    public type = input<string | undefined>(undefined);
    public poster = input<string>();

    private readonly audioRef = viewChild<ElementRef<HTMLAudioElement>>('audio');
    private readonly videoRef = viewChild<ElementRef<HTMLVideoElement>>('video');

    private player?: Plyr;

    private readonly plyrOptionsByType: Record<IackathonMediaType, Plyr.Options> = {
        audio: {
            controls: [
                'play',
                'progress',
                'current-time',
                'duration',
                'mute',
                'volume',
            ],
            i18n: {
                currentTime: 'Temps écoulé',
                duration: 'Durée',
                mute: 'Désactiver le son',
                pause: 'Pause',
                play: 'Lire',
                seek: 'Parcourir',
                unmute: 'Activer le son',
                volume: 'Ajuster le volume',
            },
            tooltips: {
                controls: true,
                seek: true,
            },
        },
        video: {
            controls: [
                'play-large',
                'play',
                'progress',
                'current-time',
                'duration',
                'mute',
                'volume',
                'fullscreen',
            ],
            i18n: {
                currentTime: 'Temps écoulé',
                duration: 'Durée',
                enterFullscreen: 'Activer le plein écran',
                exitFullscreen: 'Désactiver le plein écran',
                mute: 'Désactiver le son',
                pause: 'Pause',
                play: 'Lire',
                seek: 'Parcourir',
                unmute: 'Activer le son',
                volume: 'Ajuster le volume',
            },
            tooltips: {
                controls: true,
                seek: true,
            },
        },
    };

    ngAfterViewInit(): void {
        this.initPlayer();
        this.syncPlayerSource();
    }

    ngOnChanges(): void {
        this.syncPlayerSource();
    }

    ngOnDestroy(): void {
        this.player?.destroy();
    }

    private initPlayer(): void {
        const target = this.mediaType() === 'video'
            ? this.videoRef()?.nativeElement
            : this.audioRef()?.nativeElement;

        if (!target) {
            return;
        }

        // Plyr's typings mix `export =` with `export default`, so its default export isn't reliably seen by the type-checker. Read the constructor (the ESM build's default export) off the namespace import and cast it to shape.
        const PlyrConstructor = (
            PlyrModule as unknown as {
                default: new (
                    target: HTMLElement | string,
                    options?: Plyr.Options
                ) => Plyr;
            }
        ).default;

        this.player = new PlyrConstructor(target, this.plyrOptionsByType[this.mediaType()]);
    }

    private syncPlayerSource(): void {
        if (!this.player) {
            return;
        }

        const source = this.type()
            ? { src: this.src(), type: this.type() }
            : { src: this.src() };

        this.player.source = this.mediaType() === 'video'
            ? {
                type: 'video',
                poster: this.poster(),
                sources: [source],
            } as Plyr.SourceInfo
            : {
                type: 'audio',
                sources: [source],
            } as Plyr.SourceInfo;
    }
}
